<template>
  <v-row class>
    <v-col cols="12" class="pb-0 d-flex align-center justify-space-between">
      <!--Search-->
      <v-autocomplete dense hide-details clearable return-object filled rounded :background-color="$vuetify.theme.dark ? '' : 'white'"
        :placeholder="$t('general.search')" prepend-inner-icon="search" class="body-1 me-5" style="max-width: 400px;" :key="searchbarKey"
        @change="onSearchChange()" v-model="searchedLeague" :items="leagues" item-text="label" item-value="id">
        <template v-slot:selection="{item, index}">
          <v-chip small color="lightSecondary lighten-2" class="body-1 mb-1 black--text">
            <span class="font-weight-medium">{{ item.label }}</span>
          </v-chip>
        </template>
        <template v-slot:item="{item}">
          <v-card-text class="pa-0 primary--text font-weight-medium">
            <span>{{ item.label }}</span>
          </v-card-text>
        </template>
      </v-autocomplete>
      <!--League in progress-->
      <v-alert dense class="ma-0 py-1 align-self-start primary--text">
        <v-icon x-small color="green" class="mr-1">fa-square</v-icon>
        <span class="body-2">= {{ $t("general.activeLeague") }}</span>
      </v-alert>
    </v-col>
    <v-col>
      <v-card elevation="0" outlined>
        <v-card-title class="pt-1 pb-3" :class="$vuetify.theme.dark ? '' : 'primary'">
          <!--Areas-->
          <v-tabs v-model="areaSelected" :background-color="$vuetify.theme.dark ? '' : 'primary'" slider-color="white" dark>
            <v-tab :value="0" v-show="top5Leagues.length">
              <span class="monospace-font" :class="$vuetify.theme.dark ? 'primary--text' : 'white--text'">TOP 5</span>
            </v-tab>
            <v-tab v-for="(area, i) in leagueAreas" :key="area.name" :value="area.order" v-show="availableLeagueAreas.some(a => a.areaName === area.areaName)">
              <span class="monospace-font" :class="$vuetify.theme.dark ? 'primary--text' : 'white--text'">
                {{ area.name }}
              </span>
            </v-tab>
            <v-tab :value="11" v-show="nationalLeagues.length">
              <span class="monospace-font" :class="$vuetify.theme.dark ? 'primary--text' : 'white--text'">{{ $t("leagueTypes.nationalTeam") }}</span>
            </v-tab>
            <v-tab :value="12" v-show="otherLeagues.length">
              <span class="monospace-font" :class="$vuetify.theme.dark ? 'primary--text' : 'white--text'">{{ $t("gbe.other") }}</span>
            </v-tab>
          </v-tabs>
        </v-card-title>
        <v-divider color="white"></v-divider>
        <v-tabs-items v-model="areaSelected">
          <!-- Top 5 -->
          <v-tab-item :value="0" class="px-5 py-2" v-show="top5Leagues.length">
            <LeagueSimpleTab
              :leagues="top5Leagues"
              :showSeasons="showSeasons"
              :showActiveStyle="showActiveStyle"
              @leagueClicked="onLeagueClick"
              @yearClicked="onYearClick"
            />
          </v-tab-item>
          <!-- Areas -->
          <v-tab-item v-for="(area, i) in leagueAreas" :key="area.name" :value="area.order" v-show="availableLeagueAreas.some(a => a.areaName === area.areaName)">
            <LeagueSectionTabs :key="sectionTabsKey"
              :areaName="area.areaName"
              :groupedSections="groupedLeagues[area.areaName]"
              :availableSections="sections"
              @leagueClicked="onLeagueClick($event)"
              :selectedLeague="selectedLeagueObject"
              :searchedLeague="searchedLeague"
              :showSeasons="showSeasons"
              @yearClicked="onYearClick($event)"
              v-if="availableLeagueAreas.some(a => a.areaName === area.areaName)"
            />
          </v-tab-item>
          <!-- National Team -->
          <v-tab-item :value="11" class="px-5 py-2" v-show="nationalLeagues.length">
            <LeagueSimpleTab
              :leagues="nationalLeagues"
              :showSeasons="showSeasons"
              :showActiveStyle="showActiveStyle"
              @leagueClicked="onLeagueClick"
              @yearClicked="onYearClick"
            />
          </v-tab-item>
          <!-- Other Competitions -->
          <v-tab-item :value="12" class="px-5 py-2" v-show="otherLeagues.length">
            <LeagueSimpleTab
              :leagues="otherLeagues"
              :showSeasons="showSeasons"
              :showActiveStyle="showActiveStyle"
              @leagueClicked="onLeagueClick"
              @yearClicked="onYearClick"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>

import LeagueService from '../../../services/league.service';
import DriblabCountryFlag from '../../base/DriblabCountryFlag';
import LeagueSectionTabs from './LeagueSectionTabs.vue';
import GeneralFilter from '../../base/GeneralFilter.vue';
import LeagueSimpleTab from './LeagueSimpleTab.vue';
import * as _ from 'lodash';

export default {
  name: "LeagueSelector",
  props: {
    showSeasons: {
      //Show season years when clicking on a league
      type: Boolean,
      default: false
    },
    isHandlingLeagueSelection: {
      //Inner track of selected league
      type: Boolean,
      default: false
    }
  },
  components: {
    DriblabCountryFlag,
    LeagueSectionTabs,
    GeneralFilter,
    LeagueSimpleTab
  },
  title () {
    if (this.selectedLeagueObject) {
      return this.selectedLeagueObject.label;
    } else {
      return this.$t("app.leagues");
    }
  },
  data() {
    return {
      loading: false,
      leagues: [],
      selectedLeague: null,
      selectedLeagueObject: null,
      overrideWindowTitle: true,
      areaSelected: null,
      leagueAreas: [
        { areaName: "Europe", order: 1, name: "UEFA", areas: [0, 1, 2] },
        { areaName: "South America", order: 2, name: "CONMEBOL", areas: [3, 4] },
        { areaName: "North America", order: 3, name: "CONCACAF", areas: [5] },
        { areaName: "Africa", order: 4, name: "CAF", areas: [7] },
        { areaName: "Asia", order: 5, name: "AFC", areas: [6] },
        { areaName: "National Team", order: 6, name: this.$t("leagueTypes.nationalTeam"), areas: [10] },
        { areaName: "Youth", order: 7, name: this.$t("general.youth"), areas: [8, 9] },
        { areaName: "Women", order: 8, name: this.$t("playerScout.women"), areas: [12] },
        { areaName: "Other Competitions", order: 9, name: this.$t("gbe.other"), areas: [11] },
        { areaName: "Unknown", order: 10, name: "Unknown", areas: [99] },
      ],
      groupedLeagues: null,
      searchedLeague: null,
      sectionTabsKey: 0,
      searchbarKey: 10,
      sections: [0, 1, 2, 3, 4, 5, 6, 7], //Continental, 1st, 2nd, 3rd, 4th, 5th, National Team, Other
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    availableLeagueAreas() {
      if (this.groupedLeagues) {
        const areas = Object.keys(this.groupedLeagues);
        const filteredAreas = areas.filter(area => area !== 'National Team' && area !== 'Other Competitions');
        return filteredAreas
          .map(areaName => {
            const leagueArea = this.leagueAreas.find(area => area.areaName === areaName) || {}; // Avoid undefined errors
            return {
              areaName: areaName,
              order: leagueArea.order ? leagueArea.order : 99,
              name: leagueArea.name || areaName
            };
          })
          .sort((a, b) => a.order - b.order);
      } else return [];
    },
    nationalLeagues() {
      if (this.groupedLeagues && this.groupedLeagues['National Team']) {
        return _.flatMap(Object.values(this.groupedLeagues['National Team']));
      } else return [];
    },
    otherLeagues() {
      if (this.groupedLeagues && this.groupedLeagues['Other Competitions']) {
        return _.flatMap(Object.values(this.groupedLeagues['Other Competitions']));
      } else return [];
    },
    top5Leagues() {
      //Premier League, La Liga, Bundesliga, France Ligue 1, Serie A
      if (this.leagues.length) {
        return _.chain(this.leagues)
        .filter(l => [1923, 1, 4129, 7662, 7].includes(l.id))
        .orderBy(["isHired", "ranking", "label"], ["desc", "asc", "asc"])
        .value();
      } else return [];
    },
  },
  async mounted() {
    this.loading = true;
    await this.loadLeagueData();

    if (this.showSeasons) {
      this.selectDefaultSeasons();
    } else if (this.isHandlingLeagueSelection) {
      this.selectDefaultLeague();
      this.onLeagueClick(this.selectedLeagueObject);
    }; 

    this.loading = false;
  },
  methods: {
    async loadLeagueData() {
      const leaguesResponse = await LeagueService.getAllLeagues();
      // Add areaName, set section, then order by elo, division, and area
      this.leagues = _.orderBy(
        leaguesResponse.data.map(league => {
          const areaObj = this.leagueAreas.find(area => area.areas.includes(league.area));
          return {
            ...league,
            areaName: areaObj ? areaObj.areaName : "Unknown",
            tabSection: this.getTabSectionIndex(league)
          };
        }),
        [(l) => !l.elo ? 0 : l.elo, 'division', 'area'],
        ['desc', 'asc', 'asc']
      );

      this.groupedLeagues = _.mapValues(
        // Group by ‘areaName’ 
        _.groupBy(this.leagues, 'areaName'),
        // Sort each group by ‘area’ and then group by ‘tabSection’
        areaGroup => _.mapValues(
          _.groupBy(_.orderBy(areaGroup, ['area'], ['asc']), 'tabSection'),
          // Sort each tabSection by elo (descending), isYouth (ascending), ageLimit (descending), leagueTypeId (ascending) and label (ascending)
          tabSectionGroup => _.orderBy(tabSectionGroup, [item => !item.elo ? 0 : item.elo, 'isYouth', item => !item.ageLimit ? 0 : item.ageLimit, 'leagueTypeId', 'label'],
          ['desc', 'asc', 'desc', 'asc', 'asc'])
        )
      );
    },
    onLeagueClick(league) {
      if (this.isHandlingLeagueSelection) {
        this.selectedLeague = league.id;
        this.selectedLeagueObject = league;
      };
      this.$emit('leagueClicked', league);
    },
    onYearClick(event) {
      this.onSearchClear();
      this.$emit('yearClicked', event);
    },
    onImageLoadFailure(event) {
      event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png";
    },
    onSearchChange() {
      if (this.searchedLeague) {
        if (this.isHandlingLeagueSelection) {
          this.selectedLeagueObject = this.searchedLeague;
          this.selectedLeague = this.searchedLeague.id;
        };
        this.areaSelected = this.leagueAreas.find(areaObj => areaObj.areas.includes(this.searchedLeague.area)).order;
        this.sectionTabsKey++;
        this.onLeagueClick(this.searchedLeague);
      };
    },
    getTabSectionIndex(league) {
      const { leagueTypeId, isYouth, ageLimit, division } = league;
      const continentalIds = [2, 5];
      const nationalIds = [3, 9, 10];
      // Continental section
      if (continentalIds.includes(leagueTypeId)) {
        return 0;
      };
      // National team section
      if (nationalIds.includes(leagueTypeId)) {
        return 6;
      };
      // Youth tab
      if (isYouth) {
        if (ageLimit >= 21) return 1; // U23-U21
        if (ageLimit >= 19) return 2; // U20-U19
        if (ageLimit >= 17) return 3; // U18-U17
        if (ageLimit >= 15) return 4; // U16-U15
        if (ageLimit >= 12) return 5; // U14-U12
        if (!ageLimit) return 7;
      };
      // Regular competitions
      if (leagueTypeId === 1) {
         return division; // 1st division, 2nd division, etc
      };
      // Other section
      return 7;
    },
    onSearchClear() {
      this.searchedLeague = null;
      this.searchbarKey++;
    },
    selectDefaultSeasons() {
      const getLatestSeason = (league) => ({
        year: _.max(league.years),
        league
      });

      const getMatchingLeagues = (condition) => 
        _.chain(this.leagues)
          .filter(condition)
          .orderBy(["ranking", "label"], ["asc", "asc"])
          .map(getLatestSeason)
          .filter(s => s.league.isRegular)
          .value();

      // user.league or user.team.leagueId
      let defaultSeasons = getMatchingLeagues(l =>
        (this.currentUser.league && l.id === this.currentUser.league.id) ||
        (this.currentUser.team && l.id === this.currentUser.team.leagueId)
      );

      // Country and division <= 1
      if (_.isEmpty(defaultSeasons)) {
        const countryCode = _.get(this.currentUser, "team.leagueCountryCode", _.get(this.currentUser, "league.countryCode"));
        defaultSeasons = getMatchingLeagues(l =>
          l.countryCode === countryCode && l.division <= 1
        );
      }

      // Last two ordered by ranking and label
      if (_.isEmpty(defaultSeasons)) {
        defaultSeasons = _.chain(this.leagues)
          .orderBy(["ranking", "label"], ["asc", "asc"])
          .take(2)
          .map(getLatestSeason)
          .filter(s => s.league.isRegular)
          .value();
      }

      // Apply selection
      defaultSeasons.forEach(ds => {
        this.onYearClick({ year: ds.year, league: ds.league });
      });
    },
    showActiveStyle(league) {
      return (this.searchedLeague && this.searchedLeague.id === league.id) || (this.selectedLeagueObject && this.selectedLeagueObject.id === league.id);
    },
    selectDefaultLeague() {
      this.selectedLeagueObject = this.currentUser.league ? this.leagues.find(l => l.id === this.currentUser.league.id) : null;
      this.selectedLeague = this.currentUser.league ? this.currentUser.league.id : null;
      if (!this.selectedLeagueObject) {
        this.selectedLeagueObject = _.sample(this.leagues);
        this.selectedLeague = this.selectedLeagueObject.id;
      };
      this.areaSelected = this.top5Leagues.length && this.top5Leagues.some(l => l.id === this.selectedLeague) ? 0 
        : this.leagueAreas.find(areaObj => areaObj.areas.includes(this.selectedLeagueObject.area)).order;
    }
  },
};
</script>



// WEBPACK FOOTER //
// src/views/league/league-selector/LeagueSelector.vue